/**
 * Tray dropdown contents — rendered inside a `Popover` triggered by
 * `NotificationSidebarItem`. Header has the connection status dot +
 * Mark-all-read + Unread-only toggle. Body lists the most recent
 * notifications. Footer pins a "Go to Notifications admin" CTA.
 *
 * State lives in `useNotificationStream` (passed in via props so the
 * sidebar bell badge stays in lockstep with the visible list).
 */
import React from 'react';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ArrowRight, MailCheck, RefreshCcw } from 'lucide-react';
import { useNavigate } from 'react-router';
import { useTranslation } from '@/modules/shared/i18n/useTranslation';
import { countFor, useInboxCountsByCategory } from '@/modules/notifications/list/useInboxCounts';
import { INBOX_DEFAULT_TAB, INBOX_TABS, type InboxTab } from '@/modules/notifications/shared/constants';
import { INBOX_TAB_ICONS } from '@/modules/notifications/shared/tabIcons';
import { TAB_LIST_CLASSES, TAB_TRIGGER_CLASSES_COMPACT } from '@/modules/notifications/shared/tabStyles';
import { en, th } from './locales';
import NotificationItem from './NotificationItem';
import { resolveTarget } from './notificationTarget';
import type { NotificationLike, UseNotificationStreamResult } from './useNotificationStream';
import { testIds } from '@/test/testIds';

interface Props {
  stream: UseNotificationStreamResult;
  onClose: () => void;
}

const matchesTab = (n: NotificationLike, tab: InboxTab): boolean => {
  const sub = (n.subcategory || INBOX_DEFAULT_TAB) as InboxTab;
  return sub === tab;
};

const NotificationTray: React.FC<Props> = ({ stream, onClose }) => {
  const { t } = useTranslation({ en, th });
  const navigate = useNavigate();
  const [unreadOnly, setUnreadOnly] = React.useState(false);
  // The tray opens on Assignment (most actionable for admins); 'general'
  // stays the INBOX_DEFAULT_TAB so subcategory-less rows still bucket there.
  const [activeTab, setActiveTab] = React.useState<InboxTab>('assignment');

  // Authoritative per-tab counts from the aggregated endpoint. The query
  // is keyed by `is_unread`, shared with the admin-list tabs (one HTTP
  // round-trip total), and invalidated by `useNotificationStream` on every
  // live arrival / mark-read so badges tick within a few hundred ms.
  const { data: countsData } = useInboxCountsByCategory({ is_unread: true });

  // Fallback: until the REST round-trip lands (cold cache), compute counts
  // from the SSE-hydrated stream so the badges don't flash empty.
  const fallbackCounts = React.useMemo(() => {
    const counts = Object.fromEntries(INBOX_TABS.map((t) => [t, 0])) as Record<InboxTab, number>;
    for (const n of stream.notifications) {
      if (n.is_unread === false) continue;
      const sub = (n.subcategory || INBOX_DEFAULT_TAB) as InboxTab;
      if (sub in counts) counts[sub] += 1;
    }
    return counts;
  }, [stream.notifications]);

  const tabCounts = Object.fromEntries(
    INBOX_TABS.map((tab) => [tab, countsData ? countFor(countsData, tab) : fallbackCounts[tab]]),
  ) as Record<InboxTab, number>;

  const visible = React.useMemo(() => {
    const tabbed = stream.notifications.filter((n) => matchesTab(n, activeTab));
    return unreadOnly ? tabbed.filter((n) => n.is_unread !== false) : tabbed;
  }, [stream.notifications, activeTab, unreadOnly]);

  const handleItemClick = async (n: NotificationLike) => {
    // Mark-read first so the post-navigate view doesn't re-show the row as
    // unread on hydrate. Await keeps ordering deterministic; the call is
    // optimistic on the client either way.
    if (n.is_unread !== false) {
      await stream.markRead(n.id);
    }
    // Same-origin target → SPA navigation so the in-memory pipeline store
    // survives and the deep-link reader can apply it. Only the target_url's
    // own query travels — extra_params are never serialised into the URL.
    const internal = resolveTarget(n.target_url, window.location.origin);
    if (internal) {
      navigate(internal.pathname + internal.search);
    } else if (n.target_url) {
      // External / unparseable target — keep the existing safe full-page nav.
      stream.navigate(n.target_url);
    } else {
      // No CTA — fall through to the inbox so the user lands somewhere actionable.
      navigate('/notification-admin?type=list');
    }
    onClose();
  };

  const handleGoToAdmin = () => {
    navigate('/notification-admin');
    onClose();
  };

  return (
    <div className="flex max-h-[min(450px,calc(100vh-32px))] w-[380px] flex-col">
      {/* Header */}
      <div className="flex items-center justify-between border-b border-border px-4 py-2">
        <div className="flex items-center gap-2">
          <span className="text-sm font-semibold text-foreground">{t('header')}</span>
          {/* Connection status dot — clickable to force reconnect when amber. */}
          <button
            type="button"
            className="flex items-center"
            onClick={() => {
              if (!stream.connected) stream.reconnect();
            }}
            aria-label={stream.connected ? t('connectionConnected') : t('connectionDisconnected')}
            title={stream.connected ? t('connectionConnected') : t('connectionDisconnected')}
            data-testid={testIds.notificationsTray.connectionStatus}
          >
            <span className={`h-2 w-2 rounded-full ${stream.connected ? 'bg-success' : 'bg-warning'}`} />
          </button>
          {!stream.connected && (
            <Button
              variant="ghost"
              size="sm"
              className="h-6 w-6 p-0"
              onClick={() => stream.reconnect()}
              aria-label={t('connectionDisconnected')}
              data-testid={testIds.notificationsTray.reconnect}
            >
              <RefreshCcw className="h-3 w-3" />
            </Button>
          )}
        </div>
        <Button
          variant="link"
          size="sm"
          className="h-7 gap-1 px-1 text-xs text-primary hover:text-primary/90"
          onClick={() => void stream.markAllRead()}
          disabled={stream.unreadCount === 0}
          data-testid={testIds.notificationsTray.markAllRead}
        >
          <MailCheck className="h-3.5 w-3.5" />
          {t('markAllRead')}
        </Button>
      </div>

      {/* Subcategory tabs — `TAB_TRIGGER_CLASSES_COMPACT` is the tighter
          variant shared with `notifications/shared/tabStyles.ts`. Same
          active-state classes as the page + inbox tabs; only typography
          + flex-share differ to fit the 380px popover. */}
      <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as InboxTab)} className="w-full">
        <TabsList className={`mx-3 my-2 w-[calc(100%-1.5rem)] justify-between ${TAB_LIST_CLASSES}`}>
          {INBOX_TABS.map((tab) => {
            const c = tabCounts[tab];
            const Icon = INBOX_TAB_ICONS[tab];
            const isActive = tab === activeTab;
            return (
              <TabsTrigger
                key={tab}
                value={tab}
                className={TAB_TRIGGER_CLASSES_COMPACT}
                data-testid={testIds.notificationsTray.tab(tab)}
              >
                <Icon className="h-3.5 w-3.5 shrink-0" />
                <span className="truncate">{t(`tabs.${tab}`)}</span>
                {c > 0 ? (
                  <span
                    className={`inline-flex h-4 min-w-[16px] shrink-0 items-center justify-center rounded-full px-1 text-2xs font-semibold leading-none ${
                      isActive ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground'
                    }`}
                  >
                    {c > 99 ? '99+' : c}
                  </span>
                ) : null}
              </TabsTrigger>
            );
          })}
        </TabsList>
      </Tabs>

      {/* Unread-only toggle */}
      <div className="flex items-center justify-between border-b border-border px-4 py-2">
        <label className="flex cursor-pointer items-center gap-2 text-xs text-muted-foreground">
          <Switch
            checked={unreadOnly}
            onCheckedChange={setUnreadOnly}
            data-testid={testIds.notificationsTray.unreadOnly}
          />
          <span>{t('unreadOnly')}</span>
        </label>
      </div>

      {/* List */}
      {/* List — 3 rows visible without scroll (each NotificationItem
          renders at ~88px = 264px total). The tray's overall max-h
          (~440px) keeps the footer pinned and forces overflow into this
          scroller rather than expanding the popover. */}
      <div className="min-h-[264px] flex-1 overflow-y-auto">
        {visible.length === 0 ? (
          <div className="flex h-full min-h-[160px] flex-col items-center justify-center gap-1 px-4 py-8 text-center">
            <span className="text-sm font-medium text-foreground">{t('emptyTitle')}</span>
            <span className="text-xs text-muted-foreground">{t('emptyHint')}</span>
          </div>
        ) : (
          visible.map((n) => <NotificationItem key={n.id} notification={n} onClick={handleItemClick} />)
        )}
      </div>

      {/* Footer CTA — `shrink-0` keeps it pinned to the tray bottom even
          when the list overflows. The list scroller above is the sole
          scroll surface; the footer is always reachable. */}
      <div className="shrink-0 border-t border-border bg-accent/30">
        <Button
          variant="ghost"
          className="h-10 py-1 w-full items-top justify-center gap-1 text-xs font-medium text-primary hover:bg-primary/10 hover:text-primary/90"
          onClick={handleGoToAdmin}
          data-testid={testIds.notificationsTray.goToAdmin}
        >
          {t('goToAdmin')}
          <ArrowRight className="h-3.5 w-3.5" />
        </Button>
      </div>
    </div>
  );
};

export default NotificationTray;
